/// <amd-module name="Repos/Views/ProjectPolicies/Components/ProjectPoliciesView" />
import * as React from "react";
import * as Resources from "Repos/Views/ProjectPolicies/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { CustomHeader, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ZeroData } from "VSSUI/ZeroData";
import { IProjectPoliciesService, IProjectPolicyItem } from "../Contracts";
import { AddProjectPoliciesPanel } from "./AddProjectPoliciesPanel";
import { DeleteProjectPoliciesDialog } from "./DeleteProjectPoliciesDialog";
import { ProjectPoliciesList } from "./ProjectPoliciesList";

export class ProjectPoliciesView extends VssComponent<{}, {}> {
    private readonly zeroDataImagePath: string;

    private readonly projectPoliciesService: IProjectPoliciesService;

    private readonly showAddPolicyPanel = new ObservableValue<boolean>(false);

    private readonly deleteDialogScope = new ObservableValue<string | null>(null);

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.zeroDataImagePath = getAssetLocation(context.pageContext, "ms.vss-code-web/project-policies-content/Content/ZeroData.svg");

        this.projectPoliciesService = context.pageContext.getService<IProjectPoliciesService>("IProjectPoliciesService");
    }

    public render(): JSX.Element {
        const { showAddPolicyPanel, deleteDialogScope, projectPoliciesService } = this;

        const { projectPoliciesLoading, projectPolicies, hasEditPermission } = projectPoliciesService;

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="project-policies-page flex-grow custom-scrollbar scroll-auto-hide">
                    <Observer {...{ projectPoliciesLoading, projectPolicies, hasEditPermission }}>
                        {() => {
                            const showZeroDataExperience = !projectPoliciesLoading.value && projectPolicies.length === 0;

                            return (
                                <>
                                    <CustomHeader key="hdr">
                                        <div className="bolt-header-content-area flex-column flex-grow flex-self-stretch">
                                            <div className="bolt-header-content-area flex-row flex-grow flex-self-stretch">
                                                <HeaderTitleArea>
                                                    <HeaderTitleRow>
                                                        <HeaderTitle titleSize={TitleSize.Large}>{Resources.ProjectPoliciesHeaderTitle}</HeaderTitle>
                                                    </HeaderTitleRow>
                                                </HeaderTitleArea>
                                                <HeaderCommandBar
                                                    items={this.getHeaderCommandBarItems(showZeroDataExperience, hasEditPermission.value)}
                                                />
                                            </div>
                                            <HeaderDescription>{Resources.ProjectPoliciesHeaderSubTitle}</HeaderDescription>
                                        </div>
                                    </CustomHeader>

                                    <div key="data" className="page-content page-content-top flex-grow rhythm-vertical-16">
                                        {showZeroDataExperience ? (
                                            this.zeroDataCard(hasEditPermission.value)
                                        ) : (
                                            <Card key="tbl" className="bolt-table-card bolt-card-white bolt-card flex-column depth-8">
                                                <ProjectPoliciesList
                                                    hasEditPermission={hasEditPermission.value}
                                                    projectPolicies={projectPolicies.value}
                                                    projectPoliciesService={projectPoliciesService}
                                                    deleteScopeOnClick={this.deleteScopeOnClick}
                                                />
                                            </Card>
                                        )}
                                    </div>
                                </>
                            );
                        }}
                    </Observer>

                    <ConditionalChildren renderChildren={showAddPolicyPanel}>
                        <AddProjectPoliciesPanel
                            projectPoliciesService={this.projectPoliciesService}
                            onDismiss={() => {
                                showAddPolicyPanel.value = false;
                                this.projectPoliciesService.reloadProjectPoliciesAsync();
                            }}
                        />
                    </ConditionalChildren>

                    <Observer {...{ deleteDialogScope }}>
                        {() =>
                            deleteDialogScope.value != null && (
                                <DeleteProjectPoliciesDialog
                                    scope={deleteDialogScope.value}
                                    projectPoliciesService={this.projectPoliciesService}
                                    onClose={() => {
                                        deleteDialogScope.value = null;
                                    }}
                                />
                            )
                        }
                    </Observer>
                </Page>
            </Surface>
        );
    }

    private zeroDataCard(hasEditPermission: boolean): JSX.Element {
        return (
            <Card key="zeroData" className="flex-center" contentProps={{ contentPadding: false }}>
                <div className="flex-column flex-center rhythm-vertical-16">
                    <ZeroData
                        imagePath={this.zeroDataImagePath}
                        imageAltText={"Image"}
                        primaryText={Resources.ZeroDataPrimaryText}
                        secondaryText={Resources.ZeroDataSecondaryText}
                    />

                    <Button
                        ariaLabel={Resources.AddProjectPolicy}
                        text={Resources.AddProjectPolicy}
                        iconProps={{ iconName: "Add" }}
                        primary={true}
                        onClick={this.addPolicyOnClick}
                        disabled={!hasEditPermission}
                        tooltipProps={{
                            text: Resources.EditProjectPolicy_NoPermission,
                            disabled: hasEditPermission // Only show tooltip if button is disabled
                        }}
                    />
                </div>
            </Card>
        );
    }

    private addPolicyOnClick = (): void => {
        this.showAddPolicyPanel.value = true;
    };

    private readonly deleteScopeOnClick = (scope: string): void => {
        this.deleteDialogScope.value = scope;
    };

    private getHeaderCommandBarItems = (showZeroDataExperience: boolean, hasEditPermission: boolean): IHeaderCommandBarItem[] => {
        const items: IHeaderCommandBarItem[] = [];

        if (!showZeroDataExperience) {
            items.push({
                id: "add",
                text: Resources.AddProjectPolicy,
                isPrimary: true,
                onActivate: this.addPolicyOnClick,
                disabled: !hasEditPermission,
                tooltipProps: {
                    text: Resources.EditProjectPolicy_NoPermission,
                    disabled: hasEditPermission // Only show tooltip if button is disabled
                }
            });
        }

        return items;
    };
}

Components.add("project-policies-view", ProjectPoliciesView);
